import { analytics } from '@helpers/analytics'
import { decode } from '@helpers/shopify'
import { getCookieByName, setCookie } from '@helpers/session'
import trackError from '@helpers/trackError'
import { facebookPixelId, facebookAdClickId } from '@helpers/products'
import { getUserEmail } from '@helpers/session/user'
import { productEventUserProperties } from '@helpers/products'
import {
  toMinorUnits,
  toOpenAiContent,
  trackOpenAiEvent,
  identifyOpenAiCustomer
} from '@helpers/openaiAds/pixel'
import { FulfillmentLocation, ProductWithVariants } from 'types/product'
import {
  Cart,
  CartDiscountAllocation,
  CartLineItem,
  CartLinesUpdateInput,
  CustomAttribute
} from 'types/checkout'
import { FilterOption } from 'types/filters'
import { EmptyObject } from 'types/helpers'
import {
  FULFILLMENT_LOCATION_FLAG,
  NO_SHOW_ATTRIBUTES_PROPS,
  ORDERED_OPTIONS
} from '@consts/index'
import { getVariantItem } from '../filters'
import {
  getProductVariantAsString,
  getProductCategory,
  getProductSkuProductId,
  getProductSkuGroupId
} from '../products'
import { CartState } from '@store/redux/reducers/cartReducer'

const transformOptionsType = <T extends { type: string }>(options: T[]) =>
  options.map(option =>
    option.type === 'swimshortsleg' ? { ...option, type: 'shortsleg' } : option
  )

function getLineItemInCart(lineItems: CartLineItem[] = [], variantId: number) {
  const lineItem = lineItems.find(lineItem =>
    lineItem.merchandise.id.includes(String(variantId))
  )

  return {
    lineItemVariantId: lineItem?.merchandise?.id,
    lineItemId: lineItem?.id,
    quantityInCart: lineItem?.quantity || 0,
    quantityAvailable: lineItem?.merchandise?.quantityAvailable || 0
  }
}

export function getTalPooledStock(
  cartLines: CartLineItem[] = [],
  productId: number
) {
  const talLines = cartLines.filter(
    line =>
      line.merchandise.product.id.endsWith(`/${productId}`) &&
      line.attributes.some(
        attr => attr.key === FULFILLMENT_LOCATION_FLAG && attr.value === 'TAL'
      )
  )

  return {
    quantityInCart: talLines.reduce((total, line) => total + line.quantity, 0),
    quantityAvailable: talLines[0]?.merchandise.quantityAvailable ?? Infinity
  }
}

export function isCartItemOutOfStock({
  lineItemInCart,
  fulfillmentLocation,
  cartLines,
  productId
}: {
  lineItemInCart: { quantityInCart: number; quantityAvailable: number }
  fulfillmentLocation?: FulfillmentLocation
  cartLines: CartLineItem[]
  productId: number
}): boolean {
  if (
    lineItemInCart?.quantityInCart > 0 &&
    lineItemInCart?.quantityInCart >= lineItemInCart?.quantityAvailable
  ) {
    return true
  }

  const talStock = getTalPooledStock(cartLines, productId)

  return (
    fulfillmentLocation === 'TAL' &&
    talStock.quantityInCart >= talStock.quantityAvailable
  )
}

export function createCartItem(
  product: ProductWithVariants,
  selectedOptions: FilterOption[],
  lineItemsInCart: CartLineItem[] = []
) {
  const newSelectedOptions = transformOptionsType(selectedOptions)
  const newProduct = {
    ...product,
    options: transformOptionsType(product.options)
  }
  const selectedVariants = getVariantItem(newProduct, newSelectedOptions)

  if (!selectedVariants.length) {
    throw new Error('no variants found')
  }

  const {
    id: variantId,
    fulfillment_location: fulfillmentLocation,
    tal_dispatch_days: talDispatchDays,
    lineItemInCart
  } = selectedVariants
    .map((variant, index) => {
      const isLastItem = index === selectedVariants.length - 1
      const {
        quantityInCart,
        quantityAvailable,
        lineItemId,
        lineItemVariantId
      } = getLineItemInCart(lineItemsInCart, variant.id)

      if (
        selectedVariants.length > 1 &&
        !isLastItem &&
        lineItemId &&
        quantityInCart >= quantityAvailable
      ) {
        return null
      }

      return {
        ...variant,
        lineItemInCart: {
          quantityInCart,
          quantityAvailable,
          lineItemId,
          lineItemVariantId
        }
      }
    })
    .filter(Boolean)[0]

  const customAttributes: CustomAttribute[] = newSelectedOptions.flatMap(
    option => {
      const key = ORDERED_OPTIONS.find(o => o.toLowerCase() === option.type)
      return key
        ? {
            key,
            value: option.value
          }
        : []
    }
  )

  return {
    lineItemInCart,
    variantId,
    fulfillmentLocation,
    talDispatchDays,
    customAttributes
  }
}

export function getCheckoutCookie() {
  return getCookieByName(`checkout_${process.env.SHOP_LOCALE}`)
}

export function getCartCookie() {
  return getCookieByName(`cart_${process.env.SHOP_LOCALE}`)
}

export function setCheckoutCookie({ value = '', expiration = 14 } = {}) {
  setCookie(`checkout_${process.env.SHOP_LOCALE}`, value, expiration)
}

export function setCartCookie({ value = '', expiration = 14 } = {}) {
  setCookie(`cart_${process.env.SHOP_LOCALE}`, value, expiration)
}

function getBasketEventProductData(product: CartLineItem | EmptyObject = {}) {
  const properties: Record<string, string | number> =
    !!product.attributes &&
    product.attributes.reduce(
      (result: Record<string, string | number>, attribute: CustomAttribute) => {
        if (NO_SHOW_ATTRIBUTES_PROPS.includes(attribute.key)) {
          return result
        }

        result[attribute.key.toLowerCase()] =
          Number(attribute.value) || attribute.value
        return result
      },
      {}
    )
  const optionTypes = Object.keys(properties).map(property => ({
    type: property
  }))
  const productVariant = getProductVariantAsString(properties)
  const { title, productType, url } = product.merchandise?.product || {}
  const { image, price, compareAtPrice, sku } = product.merchandise || {}

  return {
    brand: 'SPOKE',
    product_id: getProductSkuProductId({ sku }),
    group_id: getProductSkuGroupId({ sku }),
    category: getProductCategory(optionTypes, productType),
    price: parseFloat(price?.amount) || null,
    ...(parseFloat(compareAtPrice?.amount)
      ? { was_price: parseFloat(compareAtPrice?.amount) }
      : null),
    currency: price?.currencyCode,
    color: title,
    image_url: image?.url,
    quantity: product?.quantity,
    name: productType,
    sku,
    variant: productVariant,
    url,
    ...properties
  }
}

export function cartViewedEvent(checkout: Cart | CartState) {
  const { lines = [], id, cost } = checkout
  const eventProducts = lines.map(item => getBasketEventProductData(item))

  analytics('Cart Viewed', {
    cart_id: id,
    value: Number(cost?.totalAmount?.amount),
    currency: cost?.totalAmount?.currencyCode,
    products: eventProducts
  })
}

export function generateCheckoutIntentId() {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID()
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

// Fires on every checkout button click with no dedupe of any kind, by design:
// the pixel's previous cookie-based dedupe silently suppressed genuinely new
// checkouts, so over-counting clicks here is preferred over risking that again.
export function checkoutClickedEvent(
  checkout: Cart | CartState,
  checkoutIntentId: string
) {
  const { lines = [], id, cost, discountAllocations } = checkout
  const eventProducts = lines.map(item => getBasketEventProductData(item))
  const customerEmail =
    (checkout as Partial<Cart>).buyerIdentity?.email || getUserEmail()
  const checkoutDiscountCodeTotalValue =
    getCheckoutTotalDiscountCodeValue(discountAllocations)
  const checkoutDiscountCode = getDiscountCode(discountAllocations)

  analytics('Checkout Clicked', {
    checkout_intent_id: checkoutIntentId,
    cart_id: id,
    source: 'storefront',
    value: Number(cost?.totalAmount?.amount),
    currency: cost?.totalAmount?.currencyCode,
    products: eventProducts,
    ...(customerEmail && { email: customerEmail }),
    ...(checkoutDiscountCode && { coupon: checkoutDiscountCode }),
    ...(checkoutDiscountCodeTotalValue > 0 && {
      discount: checkoutDiscountCodeTotalValue.toFixed(1)
    }),
    ...(!!facebookPixelId && { fbp: facebookPixelId }),
    ...(!!facebookAdClickId && { fbc: facebookAdClickId })
  })
  identifyOpenAiCustomer({
    email: (checkout as Partial<Cart>).buyerIdentity?.email
  })
  trackOpenAiEvent('checkout_started', {
    type: 'contents',
    amount: toMinorUnits(cost?.totalAmount?.amount),
    currency: cost?.totalAmount?.currencyCode,
    contents: eventProducts.map(item =>
      toOpenAiContent({
        id: item.sku,
        title: item.color,
        price: item.price,
        currency: item.currency,
        quantity: item.quantity
      })
    )
  })
}

function getIsQuantityIncreased(
  product: CartLinesUpdateInput | EmptyObject = {},
  previousItems: CartLineItem[] = []
): boolean {
  return (
    Number(previousItems.find(item => item.id === product.id)?.quantity) <
    product.quantity
  )
}

function getIsQuantityDecreased(
  product: CartLinesUpdateInput | EmptyObject = {},
  previousItems: CartLineItem[] = []
): boolean {
  return (
    Number(previousItems.find(item => item.id === product.id)?.quantity) >
    product.quantity
  )
}

function getCheckoutTotalDiscountCodeValue(
  discountApplications: CartDiscountAllocation[]
) {
  const totalDiscountAmountValues: number[] = []
  discountApplications?.forEach(discount => {
    totalDiscountAmountValues.push(Number(discount.discountedAmount.amount))
  })
  const sumAllDiscountAmountValues = totalDiscountAmountValues.reduce(
    (accumulator, currentValue) => {
      return accumulator + currentValue
    },
    0
  )
  return sumAllDiscountAmountValues
}

function getDiscountCode(discountApplications: CartDiscountAllocation[]) {
  if (!discountApplications) {
    return null
  }

  const filteredDiscountsByCode = discountApplications.filter(discount => {
    if (Object.keys(discount).length > 0 && discount?.code) {
      return discount
    }
  })

  return filteredDiscountsByCode.length > 0
    ? filteredDiscountsByCode[0].code
    : null
}

export function basketEvents({
  checkout = {},
  previousItems = [],
  context = '',
  product = {}
}: {
  checkout: Cart | EmptyObject
  previousItems: CartLineItem[]
  context: string
  product: CartLinesUpdateInput | EmptyObject
}) {
  try {
    const {
      lines = [],
      id,
      checkoutUrl,
      cost: { totalAmount },
      buyerIdentity: { email: checkoutEmail },
      discountAllocations
    } = checkout
    const customerEmail = checkoutEmail || getUserEmail()

    const checkoutDiscountCodeTotalValue =
      getCheckoutTotalDiscountCodeValue(discountAllocations)
    const checkoutDiscountCode = getDiscountCode(discountAllocations)

    const props = {
      ...(customerEmail && { email: customerEmail }),
      ...(checkoutDiscountCode && { coupon: checkoutDiscountCode }),
      ...(checkoutDiscountCodeTotalValue > 0 && {
        discount: checkoutDiscountCodeTotalValue.toFixed(1)
      }),
      context,
      cart_id: id,
      checkout_url: checkoutUrl,
      total: Number(totalAmount?.amount)
    }
    const eventProducts = lines.map(item => getBasketEventProductData(item))

    if (!id) {
      return false
    }

    if (lines.length >= 1 && previousItems.length === 0) {
      const eventProps = {
        ...props,
        products: eventProducts
      }
      analytics('Basket Created', eventProps)
    }

    if (
      lines.length > previousItems.length ||
      getIsQuantityIncreased(product, previousItems)
    ) {
      const eventProduct = lines.find(
        item =>
          decode(item?.merchandise.id).id ===
          decode(product?.merchandiseId || '').id
      )
      const productEventData = getBasketEventProductData(eventProduct)
      const eventProps = {
        ...props,
        ...productEventData,
        ...(!!facebookPixelId && { fbp: facebookPixelId }),
        ...(!!facebookAdClickId && { fbc: facebookAdClickId }),
        ...productEventUserProperties(),
        quantity: 1,
        product_count: lines.length
      }
      analytics('Product Added', eventProps)
      const content = toOpenAiContent({
        id: productEventData.sku,
        title: productEventData.color,
        price: productEventData.price,
        currency: productEventData.currency
      })
      identifyOpenAiCustomer({ email: checkoutEmail })
      trackOpenAiEvent('items_added', {
        type: 'contents',
        amount: content.amount,
        currency: content.currency,
        contents: [content]
      })
    }

    if (
      lines.length < previousItems.length ||
      getIsQuantityDecreased(product, previousItems)
    ) {
      const eventProduct = previousItems.find(
        item => item?.merchandise.id === product.merchandiseId
      )
      const productEventData = getBasketEventProductData(eventProduct)
      const eventProps = {
        ...props,
        ...productEventData,
        ...(lines.length === previousItems.length && { quantity: 1 })
      }
      analytics('Product Removed', eventProps)
    }

    if (lines.length === 0 && previousItems.length > 0) {
      analytics('Basket Emptied', props)
    } else {
      analytics('Basket Updated', {
        ...props,
        products: eventProducts
      })
    }
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : 'basketEvents failed'
    const error = new Error(errorMessage)
    trackError(error, { error })
  }
}

const MAX_ORDER_TAG_LENGTH = 40 // Shopify's hard limit for order/draft-order tags

// Abbreviates a hyphenated test name to the first letter of each word, so
// `pdp-size-options` becomes `pso`. Keeps the value short and
// readable instead of embedding an opaque hash.
function abbreviateTestKey(testKey: string) {
  return testKey
    .split('-')
    .filter(Boolean)
    .map(word => word[0])
    .join('')
}

// Builds a single AB-test checkout attribute. The key keeps the full test
// name for readability, while the value uses the abbreviated test name so it
// comfortably fits within Shopify's order tag length limit. The variant name
// is what analytics buckets control vs. variant on, so it survives intact
// wherever possible — only an unusually long variant is truncated as a
// last resort to avoid emitting a tag Shopify would reject or corrupt.
function buildAbTestAttribute(testKey: string, variant: string) {
  const key = `exp-${testKey}`
  const abbreviatedTestKey = abbreviateTestKey(testKey)

  let value = `exp-${abbreviatedTestKey}-${variant}`

  const overflow = value.length - MAX_ORDER_TAG_LENGTH
  if (overflow > 0) {
    const truncatedVariant = variant.slice(0, variant.length - overflow)
    value = `exp-${abbreviatedTestKey}-${truncatedVariant}`
  }

  return { key, value }
}

export function getAbTestCheckoutAttributes(
  runningAbTests: Record<string, string>
) {
  if (!runningAbTests) {
    return []
  }

  return Object.keys(runningAbTests).map(testKey =>
    buildAbTestAttribute(testKey, runningAbTests[testKey])
  )
}
